/**
 * Signature placement coordinator.
 *
 * Detects the platform tier and delegates to the appropriate implementation:
 * - Tier 1: WordApiDesktop Shape API — floating colored TextBox (Desktop only)
 * - Tier 2: Content Controls — colored bounding box at cursor (all platforms)
 * - Tier 3: Text tags [[sig:email|name]] — universal fallback
 *
 * Each tier has automatic fallback: 1 → 2 → 3.
 */

import { useState, useCallback } from 'react'
import { getSignerColor } from './signerColors'
import {
  insertSignatureShape,
  readSignatureShapes,
  removeSignatureShapes,
  removeSignatureShape,
} from './useSignatureShapes'
import {
  insertSignatureControl,
  readSignatureControls,
  removeSignatureControls,
  removeSignatureControl,
} from './useSignatureControls'
import {
  insertSignatureTag,
  readSignatureTags,
  removeSignatureTags,
} from './useSignatureTags'
import type { SignaturePlacement } from './useSignatureTags'
import { devLog } from '../services/devLog'

export type PlacementTier = 1 | 2 | 3

export type { SignaturePlacement }

function getPlacementTier(): PlacementTier {
  try {
    if (
      typeof Office !== 'undefined' &&
      Office.context?.requirements?.isSetSupported?.('WordApiDesktop', '1.2')
    ) {
      return 1
    }
  } catch {
    // requirements API not available
  }
  // Content Controls work on all platforms (WordApi 1.1)
  return 2
}

export function useSignaturePlacement() {
  const [isPlacing, setIsPlacing] = useState(false)
  const [placementError, setPlacementError] = useState<string | null>(null)
  const [activeTier, setActiveTier] = useState<PlacementTier>(() => getPlacementTier())
  const tier = activeTier

  const placeSigner = useCallback(async (email: string, name: string, colorIndex: number) => {
    setIsPlacing(true)
    setPlacementError(null)
    try {
      const color = getSignerColor(colorIndex)

      // Try Tier 1 (Shape API) if detected
      if (tier <= 1) {
        try {
          await insertSignatureShape(email, name, color.hex, color.hexLight)
          devLog.info('Placement', `Placed ${email} via Tier 1 (Shape)`)
          return
        } catch (e) {
          devLog.warn('Placement', `Tier 1 failed: ${e} — falling back to Tier 2`)
        }
      }

      // Try Tier 2 (Content Controls)
      if (tier <= 2) {
        try {
          await insertSignatureControl(email, name, color.hex)
          devLog.info('Placement', `Placed ${email} via Tier 2 (CC)`)
          if (tier === 1) setActiveTier(2) // Downgrade for subsequent calls
          return
        } catch (e) {
          devLog.warn('Placement', `Tier 2 failed: ${e} — falling back to Tier 3`)
        }
      }

      // Tier 3 fallback (text tags — works everywhere)
      try {
        await insertSignatureTag(email, name)
        devLog.info('Placement', `Placed ${email} via Tier 3 (text tag)`)
        setActiveTier(3) // Downgrade for subsequent calls
      } catch (e) {
        devLog.error('Placement', `All tiers failed for ${email}: ${e}`)
        setPlacementError(String(e))
        throw e
      }
    } finally {
      setIsPlacing(false)
    }
  }, [tier])

  const readPlacements = useCallback(async (): Promise<SignaturePlacement[]> => {
    // Read from all tiers and merge (user may have mixed placements)
    const results: SignaturePlacement[] = []

    try {
      const shapes = await readSignatureShapes()
      results.push(...shapes)
    } catch { /* Tier 1 not available */ }

    try {
      const controls = await readSignatureControls()
      results.push(...controls)
    } catch { /* Tier 2 not available */ }

    try {
      const tags = await readSignatureTags()
      // Deduplicate: don't add tags for signers already found via shapes/controls
      const existingEmails = new Set(results.map((r) => r.email))
      for (const tag of tags) {
        if (!existingEmails.has(tag.email)) {
          results.push(tag)
        }
      }
    } catch { /* Tier 3 not available */ }

    return results
  }, [])

  const removePlacement = useCallback(async (email: string): Promise<void> => {
    // Try removing from all tiers (best effort)
    try { await removeSignatureShape(email) } catch { /* ok */ }
    try { await removeSignatureControl(email) } catch { /* ok */ }
    // Tier 3 can only remove all — skip for single removal
  }, [])

  const removePlacements = useCallback(async (): Promise<void> => {
    try { await removeSignatureShapes() } catch { /* ok */ }
    try { await removeSignatureControls() } catch { /* ok */ }
    try { await removeSignatureTags() } catch { /* ok */ }
  }, [])

  return {
    tier,
    placeSigner,
    readPlacements,
    removePlacement,
    removePlacements,
    isPlacing,
    placementError,
  }
}
